import { useEffect, useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import httpService from "../services/httpService";
import posthog from 'posthog-js';
import { useUiStore } from "../store/store";

import Alert from 'react-bootstrap/Alert';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Badge from "react-bootstrap/Badge";
import Card from 'react-bootstrap/Card';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import Button from "react-bootstrap/Button";
import OverlayTrigger from "react-bootstrap/esm/OverlayTrigger";
import Tooltip from "react-bootstrap/esm/Tooltip";

import {
  FaArrowRight, FaRegClock, FaListUl,
  FaComments, FaRegCalendarAlt, FaBullseye,
} from "react-icons/fa";

import { getImage, isFutureDate } from "../utils/podcastClubUtils";
import LoadingFade from "../components/LoadingFade";
import PodcastClubChat from "../components/Chat/PodcastClubChat";
import ListenerSkills from "../components/ListenerComponents/ListenerSkills";

import "../styles/dashbaords.scss";
import "../styles/ListenerEpisode.scss";   // reuse ep-sidebar, disc-avatar, etc.
import "../styles/ListenerPodcastClub.scss";


const ListenerPodcastClub = () => {
  const { guid } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [podcastClub, setPodcastClub] = useState({});
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  const listenerSubscribedToClub    = useUiStore((s) => s.listenerSubscribedToClub);
  const setListenerSubscribedToClub = useUiStore((s) => s.setListenerSubscribedToClub);

  useEffect(() => {
    if (guid) loadPodcastClub();
  }, [guid]);

  useEffect(() => {
    if (!listenerSubscribedToClub) return;
    httpService.get('/dash/listener/subscription-count')
      .then((res) => {
        if (res.data.count <= 1) setShowWelcomeModal(true);
      })
      .catch(() => {});
  }, [listenerSubscribedToClub]);

  const handleCloseWelcomeModal = () => {
    setShowWelcomeModal(false);
    setListenerSubscribedToClub(false);
  };

  const loadPodcastClub = () => {
    setError(null);
    httpService.get(`/dash/listener-podcast-club/${guid}`)
      .then((res) => {
        setPodcastClub(res.data.podcast_club);
        posthog.capture('listener_podcast_club_viewed', {
          club_guid: guid,
          club_title: res.data.podcast_club?.title ?? null,
        });
      })
      .catch((err) => setError(err.response?.data?.message || "Error loading podcast club"))
      .finally(() => setLoading(false));
  };

  const heroImage = getImage(podcastClub, 'super_wide') || getImage(podcastClub, 'tall');
  const episodes  = podcastClub.episodes || [];
  const skills    = podcastClub.skills   || [];

  return (
    <>
    <LoadingFade id="ListenerPodcastClub-component" loading={loading} stateKey={loading ? 'loading' : 'loaded'}>

      {error && <Alert variant="danger" className="m-3">{error}</Alert>}

      {/* ── Hero ──────────────────────────────────── */}
      <div
        className="lpc-hero"
        style={heroImage
          ? { backgroundImage: `url(${heroImage})` }
          : { background: 'linear-gradient(135deg, #113F67 0%, #5A9CB5 100%)' }
        }
      >
        <div className="lpc-hero__scrim">

          {/* top bar */}
          <div>
            <NavLink className="ep-hero__breadcrumb" to="/listener-dashboard">
              ← My clubs
            </NavLink>
          </div>

          {/* bottom: skills + title + episode count */}
          <div>
            {skills.length > 0 && (
              <div className="d-flex flex-wrap gap-1 mb-2">
                {skills.slice(0, 5).map(s => (
                  <Badge
                    key={s.id}
                    bg="light"
                    text="dark"
                    style={{ fontSize: '0.67rem', opacity: 0.9 }}
                  >
                    {s.title}
                  </Badge>
                ))}
              </div>
            )}
            <h1 className="lpc-hero__title">{podcastClub.title}</h1>
            {episodes.length > 0 && (
              <div className="lpc-hero__meta">
                {episodes.length} episode{episodes.length !== 1 ? 's' : ''}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ── Two-column content ─────────────────────── */}
      <div className="container-lg py-4 px-3 px-lg-4">
        <Row className="g-4">

          {/* Main column — tabs */}
          <Col lg={8}>
            <Tabs defaultActiveKey="episodes" id="podcast-club-tabs" className="mb-3">

              {/* ══ Episodes tab ═══════════════════════ */}
              <Tab
                eventKey="episodes"
                title={
                  <span>
                    <FaListUl className="me-1" />
                    Episodes
                    {episodes.length > 0 && (
                      <Badge bg="secondary" className="ms-1" style={{ fontSize: '0.62rem' }}>
                        {episodes.length}
                      </Badge>
                    )}
                  </span>
                }
              >
                {episodes.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <FaListUl style={{ fontSize: '2.5rem', opacity: 0.2 }} />
                    <p className="mt-3 mb-0">No episodes available yet.</p>
                  </div>
                ) : (
                  <Row className="g-3">
                    {episodes.map((episode, idx) => {
                      const upcoming = isFutureDate(episode.pivot?.start_date_time);
                      const img      = episode.pivot?.featured_image || episode.featured_image;
                      const title    = episode.pivot?.title || episode.title;
                      const href     = `/listener-episode/${episode.pivot?.podcast_club_id}/${episode.pivot?.podcast_episode_id}`;

                      return (
                        <Col lg={4} md={6} key={episode.id}>
                          <Card className={`lpc-ep-card h-100${upcoming ? ' lpc-ep-card--upcoming' : ''}`}>

                            {/* Image */}
                            <div className="lpc-ep-card__img-wrap">
                              {img
                                ? <img src={img} alt={title} className="lpc-ep-card__img" />
                                : <div className="lpc-ep-card__img-placeholder" />
                              }
                              <span className="lpc-ep-card__num">#{idx + 1}</span>
                              {upcoming && (
                                <span className="lpc-ep-card__status">
                                  <FaRegClock /> Upcoming
                                </span>
                              )}
                            </div>

                            <Card.Body className="d-flex flex-column p-3">
                              <h5 className="lpc-ep-card__title">{title}</h5>
                              {episode.pivot?.start_date_time && (
                                <div className="lpc-ep-card__date">
                                  <FaRegCalendarAlt />
                                  {upcoming ? 'Starts ' : ''}
                                  {new Date(episode.pivot.start_date_time).toLocaleDateString('en-US', {
                                    month: 'short', day: 'numeric', year: 'numeric',
                                  })}
                                </div>
                              )}
                              <div className="mt-auto pt-2 d-flex justify-content-end">
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip>
                                      {upcoming
                                        ? 'Available ' + new Date(episode.pivot.start_date_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                        : 'Open episode'
                                      }
                                    </Tooltip>
                                  }
                                >
                                  <Button
                                    variant={upcoming ? 'outline-secondary' : 'outline-primary'}
                                    size="sm"
                                    disabled={upcoming}
                                    href={upcoming ? undefined : href}
                                  >
                                    {upcoming ? <FaRegClock /> : <FaArrowRight />}
                                  </Button>
                                </OverlayTrigger>
                              </div>
                            </Card.Body>

                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                )}
              </Tab>

              {/* ══ Chat tab ═══════════════════════════ */}
              <Tab
                eventKey="chat"
                title={<span><FaComments className="me-1" />Chat with Podcaster</span>}
              >
                <PodcastClubChat clubGuid={guid} />
              </Tab>

              {/* ══ Your Skills tab ════════════════════ */}
              <Tab
                eventKey="skills"
                title={<span><FaBullseye className="me-1" />Your Skills</span>}
              >
                <ListenerSkills clubGuid={guid} />
              </Tab>

            </Tabs>
          </Col>

          {/* Sidebar */}
          <Col lg={4}>
            <div className="ep-sidebar">

              {/* Club info card */}
              <Card className="ep-sidebar__club-card">
                {heroImage && (
                  <img
                    src={heroImage}
                    alt={podcastClub.title}
                    className="ep-sidebar__club-card-img"
                  />
                )}
                <div className="ep-sidebar__club-card-body">
                  <div className="ep-sidebar__club-card-title">{podcastClub.title}</div>
                  {podcastClub.description && (
                    <div
                      className="lpc-sidebar__desc mt-1"
                      dangerouslySetInnerHTML={{ __html: podcastClub.description }}
                    />
                  )}
                </div>
              </Card>

              {/* Skills */}
              {skills.length > 0 && (
                <Card className="border-0 shadow-sm" style={{ borderRadius: '14px' }}>
                  <Card.Body className="p-3">
                    <div className="ep-sidebar__section-label">Skills to Practice</div>
                    <small className="text-muted">The things you'll get better at across these episodes. As you mark moments you tried in your week, they tag against these so you can see where you're actually putting work in.</small>
                    <div className="d-flex flex-wrap gap-1 mt-3">
                      {skills.map(skill => (
                        <Badge
                          key={skill.id}
                          bg="light"
                          text="dark"
                          className="border"
                          style={{ fontSize: '0.72rem', fontWeight: 500 }}
                        >
                          {skill.title}
                        </Badge>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              )}

            </div>
          </Col>

        </Row>
      </div>

    </LoadingFade>

    <Modal show={showWelcomeModal} onHide={handleCloseWelcomeModal} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Welcome to your first Podcast Club!</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
          <iframe
            src="https://www.loom.com/embed/e0ce06042c544477a7c0ad20ec55c7f4?autoplay=1"
            frameBorder="0"
            webkitallowfullscreen="true"
            mozallowfullscreen="true"
            allowFullScreen
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
            title="Welcome to Podsy"
          />
        </div>
      </Modal.Body>
    </Modal>
    </>
  );
};

export default ListenerPodcastClub;
